/**
 * Delivery send-log data — shared types, event/source metadata, and sort
 * helpers for Messaging → Delivery.
 *
 * Data source: the LIVE notification-history ops (listMessageHistory /
 * getMessageHistoryDetail) via message-history-api.ts. The fictional
 * DELIVERY_ROWS/_DGEN_* seed was DELETED 2026-07-18 per the Voyage docket
 * §2.1(b) — no baked send-log content remains in this module.
 */
import { MSG_CHANNEL } from "./messaging-shared";
import { t as i18nT } from "../../lib/i18n";

export type SuppressedReason = "message_disabled" | "recipient_suppressed" | "covered_by_plan" | "no_recipient_address";

export interface DelivEvent {
  type: string; at: string; meta?: string;
  /** Why a send was suppressed, as a NAMED reason rather than the dispatcher's
   *  internal code (CC 2026-09-16). Nullable and optional on the wire: null
   *  means the stored code maps to no reason anyone is willing to name, and the
   *  event's generic blurb is the right fallback there.
   *
   *  `no_recipient_address` is the member that did not exist before — that
   *  outcome previously carried no reason at all and reduced to null, which is
   *  why the old copy asserted a block list for every suppression and sent
   *  operators to audit the wrong thing. "No address for this channel" is not a
   *  block: nothing was withheld, there was nowhere to send. */
  suppressedReason?: SuppressedReason | null;
  /** Token names that rendered empty on THIS attempt (on the `send` event).
   *  Omitted when none blanked (CC 2026-09-03). */
  missingTokens?: string[];
}
export interface DeliveryRow {
  id: string; channel: string;
  sender: { name: string; from: string };
  recipient: { name: string; to: string };
  source: { kind: string; label: string };
  subject?: string; text?: string; html?: string;
  sent: string; events: DelivEvent[];
  /* Notification-history provenance (listMessageHistory / getMessageHistoryDetail). */
  displayId?: string; status?: string; provider?: string; credentialSource?: string;
  replyTo?: string; bodyUnavailable?: boolean;
  /** Where the shown content came from (CC 2026-09-03 §2, live on the wire):
   *  `dispatch` = the bytes the customer received, stored at hand-off;
   *  `template` = a send made before rendered content was stored, so the
   *  TEMPLATE is shown and its tokens are unresolved BY DESIGN. */
  contentSource?: "dispatch" | "template";
  /** The provider's own id for this send — evidence for a support ticket. */
  providerMessageId?: string;
  /** When a queued row is planned to go out. A queued send has no `sent`. */
  plannedAt?: string;
  /** Row-level union of tokens that rendered empty across the dispatch's
   *  attempts. On a `dispatch` row it means a customer received a message with
   *  a hole in it; on a `template` row it carries no information (unresolved
   *  tokens are the point) and is ignored. */
  hasMissingTokens?: boolean;
}

/* Tone is the dot colour, and the dot has one job: did this send succeed or
 * fail (CD 2026-09-04). So anything that left the building successfully is
 * `success` — `send`/`accepted`/`sent` were `neutral`, which drew a grey dot
 * next to "Sent" and read as "nothing happened yet". Neutral now means exactly
 * that: not yet an outcome (`queued`) or no send attempted (`cancelled`,
 * `unsubscribed`). `success` on `sent` states provider acceptance, not inbox
 * delivery — `delivered` is the stronger claim and keeps its own entry. */
export const DELIV_EVENTS: Record<string, { label: string; labelKey: string; tone: string; icon: string; blurb: string }> = {
  send: { labelKey: "messaging.deliveryData.sent", get label() { return i18nT("messaging.deliveryData.sent", "Sent"); }, tone: "success", icon: "send", blurb: "Accepted by the sending gateway." },
  delivered: { labelKey: "messaging.deliveryData.delivered", get label() { return i18nT("messaging.deliveryData.delivered", "Delivered"); }, tone: "success", icon: "check", blurb: "Confirmed delivered to the recipient's inbox / handset." },
  opened: { labelKey: "messaging.deliveryData.opened", get label() { return i18nT("messaging.deliveryData.opened", "Opened"); }, tone: "info", icon: "eye", blurb: "Recipient opened the message." },
  clicked: { labelKey: "messaging.deliveryData.linkClicked", get label() { return i18nT("messaging.deliveryData.linkClicked", "Link clicked"); }, tone: "primary", icon: "link", blurb: "Recipient clicked a link in the message." },
  soft_bounce: { labelKey: "messaging.deliveryData.softBounce", get label() { return i18nT("messaging.deliveryData.softBounce", "Soft bounce"); }, tone: "warn", icon: "warn", blurb: "Temporary failure — mailbox full or server busy. Will retry." },
  hard_bounce: { labelKey: "messaging.deliveryData.hardBounce", get label() { return i18nT("messaging.deliveryData.hardBounce", "Hard bounce"); }, tone: "danger", icon: "warn", blurb: "Permanent failure — address invalid or rejected. Suppressed." },
  // Notification-history lattice (listMessageHistory / getMessageHistoryDetail).
  queued: { labelKey: "messaging.deliveryData.queued", get label() { return i18nT("messaging.deliveryData.queued", "Queued"); }, tone: "neutral", icon: "send", blurb: "Queued for sending — no provider events yet." },
  accepted: { labelKey: "messaging.deliveryData.accepted", get label() { return i18nT("messaging.deliveryData.accepted", "Accepted"); }, tone: "success", icon: "send", blurb: "Accepted by the sending provider." },
  deferred: { labelKey: "messaging.deliveryData.deferred", get label() { return i18nT("messaging.deliveryData.deferred", "Deferred"); }, tone: "warn", icon: "warn", blurb: "Temporary failure — the provider will retry." },
  bounced: { labelKey: "messaging.deliveryData.bounced", get label() { return i18nT("messaging.deliveryData.bounced", "Bounced"); }, tone: "danger", icon: "warn", blurb: "Permanent delivery failure — the address was rejected." },
  complained: { labelKey: "messaging.deliveryData.complaint", get label() { return i18nT("messaging.deliveryData.complaint", "Complaint"); }, tone: "danger", icon: "warn", blurb: "Recipient marked the message as spam." },
  dropped: { labelKey: "messaging.deliveryData.dropped", get label() { return i18nT("messaging.deliveryData.dropped", "Dropped"); }, tone: "danger", icon: "warn", blurb: "Dropped by the provider before delivery." },
  unsubscribed: { labelKey: "messaging.deliveryData.unsubscribed", get label() { return i18nT("messaging.deliveryData.unsubscribed", "Unsubscribed"); }, tone: "neutral", icon: "x", blurb: "Recipient unsubscribed from this stream." },
  failed: { labelKey: "common.status.failed", get label() { return i18nT("common.status.failed", "Failed"); }, tone: "danger", icon: "warn", blurb: "The send failed." },
  /* Blurb neutralised 2026-09-16 (CC): it read "Not sent — the recipient is on
     the suppression list", which ASSERTS a block list as the cause of every
     suppression and sent operators to audit the wrong thing. That specific
     claim is now one of four NAMED reasons on the event
     (`recipient_suppressed`), and this blurb is only reached when the wire
     names no reason at all — where the one thing we can honestly say is that it
     was not sent and we cannot say why. */
  suppressed: { labelKey: "messaging.deliveryData.suppressed", get label() { return i18nT("messaging.deliveryData.suppressed", "Suppressed"); }, tone: "warn", icon: "x", blurb: "Not sent — the reason wasn't recorded." },
  /* Row STATUSES that no event type spells (CC 2026-09-03 §2). The badge reads
     `status`, and `sent`/`cancelled` had no entry here — `cancelled` fell back
     to the `send` entry and rendered as "Sent", which is the exact failure the
     status-not-event-type ruling exists to stop. */
  sent: { labelKey: "messaging.deliveryData.sent", get label() { return i18nT("messaging.deliveryData.sent", "Sent"); }, tone: "success", icon: "send", blurb: "Handed to the sending provider." },
  cancelled: { labelKey: "messaging.deliveryData.cancelled", get label() { return i18nT("messaging.deliveryData.cancelled", "Cancelled"); }, tone: "neutral", icon: "x", blurb: "Cancelled before it was sent." },
};

export const DELIV_SOURCES: Record<string, { icon: string; color: string }> = {
  flow: { icon: "send", color: "var(--jrni-color-primary-1, #5142C7)" },
  campaign: { icon: "chart", color: "#2D60EB" },
  ticketgroup: { icon: "ticket", color: "#916D0F" },
  onsite: { icon: "qr", color: "#027D47" },
  manual: { icon: "user", color: "#697180" },
  trigger: { icon: "refresh", color: "#0E7490" },
};

export const SOURCE_FILTER_LABELS: Record<string, string> = {
  flow: "Flows", campaign: "Campaigns", ticketgroup: "Ticket-tier flows", onsite: "On-site", manual: "Manual sends", trigger: "Triggers",
};

const _DMON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function delivTimeVal(s: string): number {
  const m = /(\d{1,2})\s+([A-Za-z]{3}),?\s+(\d{1,2}):(\d{2}):(\d{2})/.exec(s || "");
  if (!m) return 0;
  return ((_DMON.indexOf(m[2]) * 31 + +m[1]) * 24 + +m[3]) * 3600 + +m[4] * 60 + +m[5];
}

export const lastEvent = (d: DeliveryRow): DelivEvent => d.events[d.events.length - 1];

/** The row's STATUS — what the badge must read (CC 2026-09-03 §2).
 *  A scheduled reminder carries a history entry of type `send` ("Queued for
 *  delivery") while its status is `queued`, so the latest EVENT TYPE says
 *  "Sent" about a message nobody has sent. The status is the authority; the
 *  event type only survives as a fallback for rows that carry no status. */
export const delivStatus = (d: DeliveryRow): string => d.status || lastEvent(d).type;

/** Tokens that blanked, only where that MEANS something: a `dispatch` row.
 *  Empty on `template` rows by design. */
export const missingTokensOf = (d: DeliveryRow): string[] =>
  d.contentSource === "template" ? [] : Array.from(new Set(d.events.flatMap((e) => e.missingTokens ?? [])));
/** Whether to mark the row at all — the row-level flag is the list's only
 *  signal (the names arrive with the detail fetch). */
export const hasMissingTokens = (d: DeliveryRow): boolean =>
  d.contentSource !== "template" && (!!d.hasMissingTokens || missingTokensOf(d).length > 0);

/** The operator-facing sentence for a suppression reason, or "" when there is
 *  none to name — callers then fall back to the raw dispatcher code, and only
 *  then to the event's blurb.
 *
 *  The `Object.hasOwn` guard is kept for correctness on its own terms, but it
 *  changes NO rendered output in this resolver and a test asserting otherwise
 *  would pass with it removed (checked 2026-09-16, CC raised the same finding
 *  on their side). The mechanism: a reason of `"constructor"` indexes to the
 *  inherited FUNCTION, whose `.key` and `.en` are both `undefined`, and
 *  `t(undefined, undefined)` returns its `key` argument — `undefined`, which is
 *  falsy — so the chain already falls through correctly. It earns its place
 *  because that inertness depends on two unrelated details (reading two
 *  properties off the entry, and `t`'s no-key-no-fallback return); a later
 *  resolver that hardcoded a default string would render blank without it. */
const SUPPRESSED_REASON_COPY: Record<SuppressedReason, { key: string; en: string }> = {
  message_disabled: { key: "shell.deliveryLog.reasonMessageDisabled", en: "Not sent — this message is switched off." },
  recipient_suppressed: { key: "shell.deliveryLog.reasonRecipientSuppressed", en: "Not sent — the recipient is on the suppression list or withheld consent." },
  covered_by_plan: { key: "shell.deliveryLog.reasonCoveredByPlan", en: "Not sent — a newer message already covers this send." },
  no_recipient_address: { key: "shell.deliveryLog.reasonNoRecipientAddress", en: "Not sent — there was no address for this channel. Nothing was withheld; there was nowhere to send." },
};
export const suppressedReasonText = (e: DelivEvent): string => {
  const r = e.suppressedReason;
  if (!r || !Object.hasOwn(SUPPRESSED_REASON_COPY, r)) return "";
  const entry = SUPPRESSED_REASON_COPY[r];
  return i18nT(entry.key, entry.en);
};

/** The raw dispatcher code on an event, or "" when none was recorded.
 *
 *  `meta` is set DELIBERATELY by the worker — "meta still carries the raw
 *  dispatcher code so the timeline loses no detail; suppressedReason is the
 *  reduction a client can branch on" — and it is the only place six stored
 *  codes appear at all (company_suspended, no_credential, synthetic_phone,
 *  missing_source_context and two legacy supersede codes). Those map to no
 *  named reason, so `suppressedReason` is null for them even though the code IS
 *  recorded and IS on the wire. Dropping meta would tell an operator the reason
 *  was not recorded about a suppression whose reason is sitting in the payload.
 *
 *  The literal label is NOT a code. The worker falls back to the bare string
 *  "Suppressed" when it has nothing else, so `meta` is always truthy — which
 *  means a plain `reason || meta || blurb` chain never reaches the blurb and
 *  renders the word "Suppressed" as the explanation of itself. Filtering that
 *  one value is what makes the nothing-recorded branch reachable, and so makes
 *  "the reason wasn't recorded" true when it shows. */
export const suppressedCodeOf = (e: DelivEvent): string => {
  const raw = (e.meta ?? "").trim();
  if (!raw) return "";
  return raw.toLowerCase() === "suppressed" ? "" : raw;
};

/** The statuses a row can hold — the badge and the status filter's vocabulary.
 *  Event types (opened / clicked / …) are timeline entries, not row states. */
export const DELIV_ROW_STATUSES = ["queued", "sent", "delivered", "bounced", "failed", "suppressed", "cancelled"];

export function sortDeliveries(arr: DeliveryRow[], sort: { key: string; dir: "asc" | "desc" }): DeliveryRow[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const val = (d: DeliveryRow): string | number => {
    switch (sort.key) {
      case "recipient": return d.recipient.name.toLowerCase();
      case "channel": return MSG_CHANNEL(d.channel).name.toLowerCase();
      case "message": return (d.subject || d.text || "").toLowerCase();
      case "sender": return d.sender.name.toLowerCase();
      case "source": return d.source.label.toLowerCase();
      case "latest": default: return delivTimeVal(lastEvent(d).at);
    }
  };
  return arr.slice().sort((a, b) => {
    const va = val(a), vb = val(b);
    if (typeof va === "number") return (va - (vb as number)) * dir;
    return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
  });
}
